import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

import { AnalyticsContext } from '../../../analytics/AnalyticsContext';

import useLockBodyScroll from '../../../hooks/useLockBodyScroll/useLockBodyScroll';

import { CloseIcon } from '../../Icons';

import './Modal.scss';
import { Heading } from '../../a11y';
import useAnalytics from '../../../hooks/useAnalytics';

interface ISearchOverlayProps {
  isOpen: boolean;
  title?: string;
  ariaLabel?: string;
  type: 'overlay' | 'dialog';
  focusTrapWatchList: any[];
  onClose: () => void;
  children: ReactNode;
  className?: string;
  copy?: string | ReactNode;
}

const Modal = ({
  isOpen,
  type,
  title = '',
  ariaLabel,
  focusTrapWatchList = [],
  onClose,
  children,
  className = '',
  copy = '',
}: ISearchOverlayProps) => {
  // gatsby document workaround
  const portalRoot =
    typeof document !== `undefined` ? document.getElementById('portal') : null;

  const modalRef = useRef<HTMLDivElement | null>(null);

  const analytics = useAnalytics();

  // prevents scrolling behind overlay
  useLockBodyScroll(isOpen);

  // closes modal and tracks close event quantum
  const handleClose = useCallback(() => {
    onClose();
    analytics.track('modalClose', {
      msgTriggeredBy: 'user',
    });
  }, [onClose, analytics]);

  // handle setting/removing classes for styling and functionality
  useEffect(() => {
    portalRoot?.classList.add('open');
    if (type === 'dialog') {
      modalRef?.current?.focus();
      portalRoot?.classList.add('dialog-open');
    }

    return () => {
      portalRoot?.classList.remove('open');
      type === 'dialog' && portalRoot?.classList.remove('dialog-open');
    };
  }, [modalRef, isOpen, portalRoot, type]);

  // close overlay when 'ESC' key is pressed
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      e.key === 'Escape' && handleClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [handleClose]);

  // click overlay to close modal
  useEffect(() => {
    const portalRoot = document.getElementById('portal');
    const handleOverlayClick = (e) => {
      if (e.target === portalRoot) {
        handleClose();
      }
    };
    portalRoot?.addEventListener('click', handleOverlayClick);
    return () => {
      portalRoot?.removeEventListener('click', handleOverlayClick);
    };
  }, [handleClose]);

  // traps focus within modal, you can pass in a list of watcher for the use effect to update
  useEffect(() => {
    if (modalRef?.current && isOpen) {
      const focusable: HTMLElement[] = Array.from(
        modalRef?.current.querySelectorAll(
          'button:not([tabindex="-1"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), video'
        )
      );

      if (focusable?.length) {
        const firstElement: HTMLElement = focusable[0];
        const lastElement: HTMLElement = focusable[focusable.length - 1];

        const trapFocus = (e: KeyboardEvent) => {
          if (e.key === 'Tab') {
            if (e.shiftKey) {
              if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
              }
            } else {
              if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
              }
            }
          }
        };

        modalRef.current.addEventListener('keydown', trapFocus);

        const storedRef = modalRef.current;
        return () => {
          storedRef?.removeEventListener('keydown', trapFocus);
        };
      }
    }
  }, [modalRef, isOpen, ...focusTrapWatchList]);

  return (
    portalRoot &&
    createPortal(
      <motion.div
        role="dialog"
        aria-modal="true"
        className={`${type} ${className}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, type: 'tween' }}
        tabIndex={-1}
        aria-label={ariaLabel || title}
        ref={modalRef}
        onFocus={() => modalRef.current?.setAttribute('tabIndex', '-1')}
      >
        {/* Ex: SearchOverlay */}
        {type === 'overlay' && (
          <>
            <header className="overlay__header">
              {title && (
                <Heading id="modal-label" className="overlay__title">
                  {title}
                </Heading>
              )}
              <button
                onClick={handleClose}
                className="overlay__close"
                title="Close"
              >
                <CloseIcon />
              </button>
            </header>
            {typeof copy === 'string' ? (
              <p className="overlay__copy">{copy}</p>
            ) : (
              copy
            )}
            <div className="overlay__body">{children}</div>
          </>
        )}

        {/* Ex: Video Modal in BannerAdStatic section */}
        {type === 'dialog' && (
          <>
            <button
              onClick={handleClose}
              className="dialog__close"
              title="Close"
            >
              <CloseIcon />
            </button>
            {children}
          </>
        )}
      </motion.div>,
      portalRoot
    )
  );
};

export default Modal;
